// =====================================================================
// CONFIRMA QUE ERES TÚ · DIRECTRIZ-LLAVE L-3
// =====================================================================
// El diálogo que se abre antes de agregar o quitar una llave de acceso
// cuando no hay una confirmación de menos de 5 minutos. Pide la
// contraseña y, si la persona tiene el autenticador puesto, el código.
//
// `pideCodigo`: true / false si se sabe, null si no se pudo saber. Sin
// saberlo se enseña el campo del código marcado «si usas autenticador»:
// el servidor solo lo mira si la persona lo tiene (R-LL-6), y pedir de
// más es mejor que dejarla atascada sin el campo.
import { useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import Modal from '../ui/Modal';
import CampoContrasena from '../ui/CampoContrasena';
import Ayuda from '../ui/Ayuda';
import { confirmarIdentidad } from './llaveDeAcceso';

export default function ConfirmarIdentidad({ pideCodigo = null, aviso = null, onListo, onCerrar }) {
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const conCodigo = pideCodigo !== false;
  const codigoLimpio = codigo.replace(/\s+/g, '');
  const falta = [
    !password && 'escribe tu contraseña',
    pideCodigo === true && codigoLimpio.length < 6 && 'escribe el código de tu autenticador',
  ].filter(Boolean).join(' y ');
  const listo = !falta && !enviando;

  async function confirmar(e) {
    e?.preventDefault();
    if (!listo) return;
    setEnviando(true);
    setError(null);
    try {
      await confirmarIdentidad(password, conCodigo ? codigoLimpio : '');
      onListo();
    } catch (err) {
      setError(err.message);
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Confirma que eres tú"
      icono={ShieldCheck}
      onCerrar={onCerrar}
      bloqueado={enviando}
      pie={(
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 p-4">
          {falta && <p id="identidad-falta" role="note" className="mr-auto text-xs text-gray-500">Para seguir, {falta}.</p>}
          <button type="button" onClick={onCerrar} disabled={enviando}
                  className="min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" form="form-confirmar-identidad" disabled={!listo} data-accion="confirmar-identidad"
                  aria-describedby={falta ? 'identidad-falta' : undefined}
                  className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-[var(--color-marca)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {enviando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {enviando ? 'Confirmando…' : 'Confirmar'}
          </button>
        </div>
      )}
    >
      <form id="form-confirmar-identidad" onSubmit={confirmar} className="space-y-3">
        <p className="text-sm text-gray-600">
          {pideCodigo === false
            ? 'Para agregar o quitar una llave de acceso, escribe tu contraseña. La confirmación vale 5 minutos.'
            : 'Para agregar o quitar una llave de acceso, escribe tu contraseña y el código de tu autenticador. La confirmación vale 5 minutos.'}
        </p>
        {aviso && <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-900">{aviso}</p>}
        <div>
          <label htmlFor="identidad-password" className="mb-1 block text-sm font-medium">Contraseña</label>
          <CampoContrasena id="identidad-password" value={password} autoFocus
                           onChange={(e) => setPassword(e.target.value)} className="entrada" />
        </div>
        {conCodigo && (
          <div>
            <label htmlFor="identidad-codigo" className="mb-1 block text-sm font-medium">
              {pideCodigo === null ? 'Código de tu autenticador (si lo usas)' : 'Código de tu autenticador'}
            </label>
            <input id="identidad-codigo" value={codigo} inputMode="text" autoComplete="one-time-code"
                   maxLength={32} onChange={(e) => setCodigo(e.target.value)}
                   className="entrada max-w-xs font-mono" placeholder="123456" />
            <Ayuda id="seguridad.llaves.identidad" />
          </div>
        )}
        {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{error}</p>}
      </form>
    </Modal>
  );
}
